import moment from 'moment-timezone';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { ScheduleParting } from 'sonata-platform-components';
import { Toast, useQueryFetch } from 'sonata-react-components';
import { getInsertionOrderCalendarInfo, InsertionOrder } from 'sonata-static-storage/lib/entities';
import { PricingModelEnum } from 'sonata-static-storage/lib/schemas/campaign/bidModel.schema';

import { DeliveryStrategySection } from '@/components/ioSettings/DeliveryStrategySection';
import { FlightsSummary } from '@/components/ioSettings/InsertionOrderForm/FlightSettings/FlightsSummary/FlightsSummary';
import { toFormattedLocalDate } from '@/helpers/common/date.helper';
import DatePicker from './datePicker/DatePicker';

import './scheduleAndDistribution.styles.scss';

import { CalendarSummary } from '../CalendarSummary/CalendarSummary';

import type { MultiFlightEntityType } from 'sonata-static-storage/lib/common/common.types';
import type { ContextualBiddingInterface } from 'sonata-static-storage/lib/schemas/campaign/contextualBidding.schema';
import type { FrequencyCapping } from 'sonata-static-storage/lib/schemas/multiFlight/frequencyCapping.schema';
import type { InsertionOrderFlight } from 'sonata-static-storage/lib/schemas/multiFlight/insertionOrderFlight.schema';
import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';
import type { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';
import type { LineItemSchedule } from '@/components/ioSettings/InsertionOrderForm/hooks/useIoLineItemSchedules';

type Props = {
  beginDate?: Date | null;
  completed?: boolean;
  contextualBidding?: ContextualBiddingInterface;
  endDate?: Date | null;
  entityType: MultiFlightEntityType;
  errors: Record<string, any>;
  flights?: InsertionOrder['flights'];
  id?: string;
  /** Line items only: flights of the line item's IO, shown in its date picker. */
  ioFlights?: InsertionOrderFlight[];
  /** Line items only: line-item schedules of the line item's IO, shown in its date picker. */
  ioLineItems?: LineItemSchedule[];
  isDooh?: boolean;
  /** Line items only: name of the line item being edited (its day lines' tooltip row). */
  lineItemName?: string;
  onChangeLineItemDates?: (dates: { beginDate: Date | null; endDate: Date | null }) => void;
  onChangeTimezone?: (timezone: string) => void;
  onSchedulePartingChange: (scheduleParting: ScheduleParting) => void;
  onUserFrequencyCappingChange: (FrequencyCapping: FrequencyCapping) => void;
  pricingModelConfig?: InsertionOrder['pricingModelConfig'];
  scheduleParting?: ScheduleParting;
  timezone?: string;
  user: UserInterface;
  userFrequencyCapping: InsertionOrder['userFrequencyCapping'];
  userRoles: UserRoleInterface[];
};

type State = {
  showScheduleErrorToast: boolean;
  showDatesErrorToast: boolean;
};

export function ScheduleAndDistribution({
  contextualBidding,
  beginDate = null,
  completed = false,
  endDate = null,
  entityType,
  errors,
  flights,
  id,
  ioFlights,
  ioLineItems,
  isDooh = false,
  lineItemName,
  onChangeLineItemDates,
  onChangeTimezone,
  onSchedulePartingChange,
  onUserFrequencyCappingChange,
  pricingModelConfig,
  scheduleParting,
  timezone = 'Europe/Madrid',
  user,
  userFrequencyCapping,
  userRoles
}: Props) {
  const { formatMessage } = useIntl();
  const [{ showScheduleErrorToast, showDatesErrorToast }, setState] = useState<State>({
    showDatesErrorToast: true,
    showScheduleErrorToast: true
  });

  const showCalendarSummaryOfFlightsInfo = entityType === 'insertionOrder';
  const schedulePartingError = Boolean(errors?.scheduleParting);
  const datesError = Boolean(errors?.beginDate || errors?.endDate);
  const pricingModel = pricingModelConfig?.bidModels?.[0]?.bid?.pricingModel;
  const isPpsv = pricingModel === PricingModelEnum.ppsv;
  const isPpsvLineItem = isPpsv && entityType === 'lineItem';

  const schedulePartingErrorMessage = isPpsvLineItem
    ? formatMessage({
        defaultMessage:
          'You need to configure at least one hour on any day of the week and ensure that the date range contains at least 10 effective days.',
        id: 'ioSettings.errors.scheduleAndDistributionPpsv'
      })
    : formatMessage({
        defaultMessage: 'You need to configure at least one hour on any day of the week.',
        id: 'ioSettings.errors.scheduleAndDistribution'
      });

  const errorText =
    schedulePartingError && datesError
      ? formatMessage({
          defaultMessage: 'You need to configure at least one hour on any day of the week & begin and end dates.',
          id: 'ioSettings.errors.scheduleAndDistributionAndDates'
        })
      : schedulePartingError
        ? schedulePartingErrorMessage
        : formatMessage({
            defaultMessage: 'You need to configure begin and end dates.',
            id: 'ioSettings.errors.dates'
          });

  const flightsWithDatesSet = flights?.filter(({ beginDate: flightBeginDate, endDate: flightEndDate }) => {
    return !!flightBeginDate && !!flightEndDate;
  });

  const flightsTimeFrames =
    flightsWithDatesSet?.length &&
    flightsWithDatesSet.map(({ beginDate: flightBeginDate, endDate: flightEndDate }) => ({
      beginDate: flightBeginDate,
      endDate: flightEndDate
    }));

  const flightsDates =
    flights?.flatMap(frame => [
      toFormattedLocalDate(frame.beginDate as Date),
      toFormattedLocalDate(frame.endDate as Date)
    ]) || [];
  const totalDays = flights?.reduce(
    (daysAcc, flight) => moment(flight.endDate).diff(moment(flight.beginDate), 'days', false) + daysAcc,
    0
  );
  const flightsComplete = flights?.filter(f => f.beginDate && f.endDate);

  const { data: calendarInfo, isLoading: ioCalendarIsLoading } = useQueryFetch(
    ['ioCalendar', user.id, id, flightsComplete, scheduleParting],
    () => getInsertionOrderCalendarInfo(user.id!, id, flightsComplete, scheduleParting),
    {
      enabled: !!flightsComplete?.length,
      staleTime: 0
    }
  );

  const schedulePartingTimeFrames =
    entityType === 'insertionOrder'
      ? flightsTimeFrames
      : [
          {
            beginDate,
            endDate
          }
        ];

  const scrollToFlights = () => {
    const element = document.getElementById('flightSettings');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  return (
    <div className="scheduleAndDistribution">
      <div className="ioSettings__schedulingModelBox" id={'schedulingSection-box'}>
        <header className="scheduleAndDistribution__header">
          <div className="scheduleAndDistribution__title">
            {formatMessage({
              defaultMessage: 'Scheduling & Distribution',
              id: 'ioSettings.schedulingTitle'
            })}
          </div>
          <div className="scheduleAndDistribution__subtitle">
            {formatMessage({
              defaultMessage: 'Customize ad display times for maximum impact',
              id: 'ioSettings.schedulingSubtitle'
            })}
          </div>
        </header>
        <div className="scheduleAndDistribution__body">
          {(showScheduleErrorToast || showDatesErrorToast) && (schedulePartingError || datesError) && (
            <Toast
              autoClose={false}
              className="settings__errorToast"
              closeToast={() => {
                setState(prevState => ({
                  ...prevState,
                  showDatesErrorToast: false,
                  showScheduleErrorToast: false
                }));
              }}
              icon="fal fa-exclamation-triangle"
              text={errorText}
              type="danger"
            />
          )}
          {!showCalendarSummaryOfFlightsInfo && (
            <DatePicker
              beginDate={beginDate}
              completed={completed}
              endDate={endDate}
              id={id!}
              ioFlights={ioFlights}
              ioLineItems={ioLineItems}
              lineItemName={lineItemName}
              onChangeLineItemDates={onChangeLineItemDates}
              onChangeTimezone={onChangeTimezone}
              timezone={timezone}
            />
          )}
          {showCalendarSummaryOfFlightsInfo && (
            <FlightsSummary
              className="ioSettings__schedulingFlightsSummary"
              dates={flightsDates}
              subTitle={formatMessage({
                defaultMessage: '| Set in Flight Section',
                id: 'ioSettings.schedulingSummaryTitle'
              })}
              title={formatMessage({
                defaultMessage: 'Insertion Order Date',
                id: 'ioSettings.schedulingSummarySubtitle'
              })}
              total={formatMessage(
                {
                  defaultMessage: `${totalDays} days`,
                  id: 'ioSettings.schedulingDaysTotal'
                },
                { totalDays }
              )}
            />
          )}
          <ScheduleParting
            contextualBidding={contextualBidding || { enabled: false, scenarios: [] }}
            entityType={entityType}
            headerSubtitle={formatMessage({
              defaultMessage: 'Hours by day of week',
              id: 'ioSettings.schedulingCalendarSubtitle'
            })}
            pricingModel={pricingModel}
            scheduleParting={scheduleParting}
            setCampaignScheduleParting={(schedulePartingData: ScheduleParting) =>
              onSchedulePartingChange(schedulePartingData)
            }
            timeFrames={schedulePartingTimeFrames}
            timezone="Europe/Madrid"
            user={user}
            userRoles={userRoles}
            withTitle={false}
          />
          <DeliveryStrategySection
            errors={errors}
            isDooh={isDooh}
            setUserFrequencyCapping={(frequencyCapping: FrequencyCapping) =>
              onUserFrequencyCappingChange(frequencyCapping)
            }
            userFrequencyCapping={userFrequencyCapping}
          />
          {showCalendarSummaryOfFlightsInfo && (
            <CalendarSummary
              calendarInfo={calendarInfo}
              id={'ioForm-calendarSummary'}
              loading={ioCalendarIsLoading}
              onSetFlightsClick={scrollToFlights}
            />
          )}
        </div>
      </div>
    </div>
  );
}
